import AutoResizeTextarea from '@/components/AutoResizeTextarea/AutoResizeTextarea';
import DetailsCard from '@/components/DetailsCard';
import SortableItemContainer from '@/components/DragAndDrop/SortableItemContainer';
import EditableTextnput from '@/components/EditableTextInput';
import HeroImage from '@/components/HeroImage';
import ManageProjectModal from '@/components/ManageProjectModal';
import ProjectsOverviewCard from '@/components/ProjectsOverviewCard';
import PurposeSelect from '@/components/PurposeSelect';
import { PURPOSE_SELECT_BEHAVIOR_CATEGORY } from '@/components/PurposeSelect/constants';
import { RoutesList } from '@/routes/routesList';
import { cleanCategoryForMutation } from '@/services/categories';
import { useUpdateCategoryMutation } from '@/services/categories/hooks';
import { useCreateProject, useProjectsByCategoryId, useUpdateProjectsOrderBatch } from '@/services/project/hooks';
import { IconFire, IconMountainTop, IconMyProjects, IconPlus, IconStarComet } from '@/theme/icons';
import { Category } from '@/types/category';
import { ProjectById } from '@/types/project';
import { refetchCategoryAndBlocks } from '@/utils/category';
import rem from '@/utils/rem';
import {
  Button,
  Editable,
  EditablePreview,
  EditableTextarea,
  Flex,
  Grid,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  chakra,
  useDisclosure,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

type Props = {
  category?: Category | undefined | null;
};

const createM7FormSchema = (t: (key: string) => string) =>
  z.object({
    ultimateVision: z
      .string()
      .trim()
      // .min(1, { message: t('sections.magnificent7.validation.ultimateVision.required') })
      // .min(5, { message: t('sections.magnificent7.validation.ultimateVision.minLength') })
      .max(10000, { message: t('sections.magnificent7.validation.ultimateVision.maxLength') })
      .nullable()
      .optional(),
    role: z
      .string()
      .trim()
      // .min(1, { message: t('sections.magnificent7.validation.role.required') })
      // .min(5, { message: t('sections.magnificent7.validation.role.minLength') })
      .max(255, { message: t('sections.magnificent7.validation.role.maxLength') })
      .nullable()
      .optional(),
    ultimatePurpose: z
      .string()
      .trim()
      // .min(1, { message: t('sections.magnificent7.validation.ultimatePurpose.required') })
      // .min(5, { message: t('sections.magnificent7.validation.ultimatePurpose.minLength') })
      .max(10000, { message: t('sections.magnificent7.validation.ultimatePurpose.maxLength') })
      .optional(),
    oneYearGoal: z
      .string()
      .trim()
      // .min(1, { message: t('sections.magnificent7.validation.oneYearGoal.required') })
      // .min(5, { message: t('sections.magnificent7.validation.oneYearGoal.minLength') })
      .max(10000, { message: t('sections.magnificent7.validation.oneYearGoal.maxLength') })
      .nullable()
      .optional(),
    ninetyDayGoal: z
      .string()
      .trim()
      // .min(1, { message: t('sections.magnificent7.validation.ninetyDayGoal.required') })
      // .min(5, { message: t('sections.magnificent7.validation.ninetyDayGoal.minLength') })
      .max(10000, { message: t('sections.magnificent7.validation.ninetyDayGoal.maxLength') })
      .nullable()
      .optional(),
  });

type FormData = z.infer<ReturnType<typeof createM7FormSchema>>;

const MotionDiv = chakra(motion.div);

const Magnificent7Section = ({ category, ...rest }: Props) => {
  const { t } = useTranslation('CategoryManagerDetails');
  const M7FormSchema = createM7FormSchema(t);
  const toast = useToast();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(null);

  const IS_ARCHIVE_RGB = 'rgba(128, 128, 128, 0.5)';

  const isArchived = category?.isArchived;

  const [isLargerThan1024] = useMediaQuery('(min-width: 1024px)');

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, defaultValues },
  } = useForm<FormData>({
    defaultValues: {
      ultimateVision: category?.ultimateVision ?? null,
      role: category?.role ?? null,
      ultimatePurpose: category?.ultimatePurpose ?? '',
      oneYearGoal: category?.oneYearGoal ?? null,
      ninetyDayGoal: category?.ninetyDayGoal ?? null,
    },
    resolver: zodResolver(M7FormSchema),
    mode: 'onChange',
  });

  const {
    isOpen: isOpenCreateProjectModal,
    onOpen: openCreateProjectModal,
    onClose: onCloseCreateProjectModal,
  } = useDisclosure({ id: 'createNewProject' });

  const { mutateAsync: createProject, isLoading: isCreatingProject } = useCreateProject();
  const updateCategory = useUpdateCategoryMutation();
  const updateProjectsOrderBatch = useUpdateProjectsOrderBatch();

  const { data: projectByCategory } = useProjectsByCategoryId(category?.id ?? '', {
    enabled: !!category && !!category.id && category.id !== '',
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const [projectsOrder, setProjectsOrder] = useState(projectByCategory?.project?.map((p) => p.id) || []);

  useEffect(() => {
    setProjectsOrder(projectByCategory?.project?.map((p) => p.id) || []);
  }, [projectByCategory?.project]);

  const orderedProjects = projectsOrder
    .map((id) => projectByCategory?.project?.find((p) => p.id === id))
    .filter((p): p is ProjectById => Boolean(p));

  const handleDragEnd = (event: any) => {
    setActiveId(null);
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setProjectsOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        updateProjectsOrderBatch.mutate(
          newOrder.map((id, idx) => ({
            id,
            order: newOrder.length - idx,
          })),
        );
        return newOrder;
      });
    }
  };

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!category) {
        return;
      }

      updateCategory.mutate(
        {
          ...cleanCategoryForMutation(category),
          ultimateVision: data?.ultimateVision ?? null,
          role: data?.role ?? null,
          ultimatePurpose: data?.ultimatePurpose ?? '',
          oneYearGoal: data?.oneYearGoal ?? null,
          ninetyDayGoal: data?.ninetyDayGoal ?? null,
        },
        {
          onSuccess: () => {
            refetchCategoryAndBlocks();

            toast({
              title: t('sections.magnificent7.toasts.saved'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          },

          onError: (error) => {
            toast({
              title: error?.response?.errors?.[0]?.message,
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          },
        },
      );
    },
    [category, toast, updateCategory, t],
  );

  const onCreateProject = useCallback(
    async ({ name, purpose, result }: { name: string; result: string; purpose: string }) => {
      const id = uuidv4();

      if (!category?.id) return;

      await createProject({
        id,
        name,
        categoryId: category.id,
        result,
        purpose,
        imageUrl: category?.selectedImageUrl ?? '',
        isArchived: false,
      });

      navigate(`${RoutesList.ProjectsPage}/${id}`);
    },
    [category?.id, category?.selectedImageUrl, createProject, navigate],
  );

  const handleBlur = useCallback(
    (value: string | undefined | null, prevValue: string, field: keyof FormData) => {
      if (value === undefined) {
        setValue(field, prevValue);
      }
    },
    [setValue],
  );

  useEffect(() => {
    reset({
      ultimateVision: category?.ultimateVision ?? null,
      role: category?.role ?? null,
      ultimatePurpose: category?.ultimatePurpose ?? '',
      oneYearGoal: category?.oneYearGoal ?? null,
      ninetyDayGoal: category?.ninetyDayGoal ?? null,
    });
  }, [
    category?.ninetyDayGoal,
    category?.oneYearGoal,
    category?.role,
    category?.ultimatePurpose,
    category?.ultimateVision,
    reset,
  ]);

  return (
    <VStack {...rest} overflow="hidden" paddingBottom={rem(80)}>
      <MotionDiv
        initial={{ opacity: category ? 0 : 1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        width="full"
        padding={0}
      >
        <HeroImage category={category} imageUrl={category?.selectedImageUrl} fadeIn isArchived={isArchived}>
          <Stack
            justifyContent="center"
            width={{ base: '70%', '2xl': '80%', '4xl': '60%' }}
            minHeight={rem(300)}
            paddingY={rem(50)}
          >
            <Flex
              width="fit-content"
              marginBottom={rem(22)}
              padding={rem(8)}
              opacity={isArchived ? '0.4' : '1'}
              borderRadius={4}
              backgroundColor="gray.50"
            >
              <Text textStyle="smallBlack" color="blue.1000" textTransform="uppercase">
                {t('sections.magnificent7.sectionTitles.myUltimateVision')}
              </Text>
            </Flex>

            <Controller
              name="ultimateVision"
              control={control}
              render={({ field: { ref, value, onChange } }) => (
                <Editable
                  ref={ref}
                  textStyle="h2SatMedium"
                  display={isArchived && !value ? 'none' : ''}
                  overflow="unset"
                  width="full"
                  height="full"
                  fontSize={rem(50)}
                  lineHeight="110%"
                  borderRadius={rem(5)}
                  defaultValue={value ?? ''}
                  isDisabled={isArchived || isSubmitting}
                  onBlur={(preValue) => handleBlur(value, preValue, 'ultimateVision')}
                  onChange={onChange}
                  onSubmit={() => {
                    if (category?.ultimateVision !== value && value !== undefined) {
                      // submit empty string if prev value non empty, don't submit if it was empty
                      handleSubmit(onSubmit)();
                    }
                  }}
                  placeholder={t('sections.magnificent7.placeholders.ultimateVision')}
                  selectAllOnFocus={false}
                  submitOnBlur
                  value={value ?? ''}
                >
                  <EditablePreview width="full" height="full" fontWeight={600} opacity={value ? 1 : 0.5} />
                  <EditableTextarea
                    as={AutoResizeTextarea}
                    height="full"
                    fontWeight={600}
                    lineHeight={rem(55)}
                    border={`${rem(1)} solid`}
                    borderColor="transparent"
                    _focusVisible={{
                      border: '2px solid',
                      borderColor: 'cyan.400',
                      boxShadow: 'none',
                      backgroundColor: 'background-primary',
                    }}
                    resize="none"
                    onFocus={(e) =>
                      e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
                    }
                  />
                </Editable>
              )}
            />
            {errors?.ultimateVision?.message && (
              <Text as="span" color="red.500" fontSize="small">
                {errors?.ultimateVision?.message}
              </Text>
            )}
          </Stack>
        </HeroImage>

        <VStack position="relative" zIndex={10} gap={rem(16)} width="full" paddingX={[4, 8, 16]}>
          <SimpleGrid
            gap={rem(16)}
            width="full"
            columns={{
              base: 1,
              lg: 2,
            }}
          >
            <DetailsCard
              color={isArchived ? IS_ARCHIVE_RGB : category?.color}
              sectionTitle={t('sections.magnificent7.sectionTitles.myRole')}
              icon={IconFire}
            >
              {isArchived && !defaultValues?.role ? (
                <Text textStyle="smallBlack" width="100%" color="gray.400" textAlign="center">
                  {t('sections.magnificent7.emptyStates.noMyRoleAdded')}
                </Text>
              ) : (
                <EditableTextnput
                  isHidden={isArchived && !defaultValues?.role}
                  cursor={isArchived ? 'not-allowed' : undefined}
                  control={control}
                  fontSize={rem(17)}
                  isSubmitting={isArchived || isSubmitting}
                  name="role"
                  placeholder={t('sections.magnificent7.placeholders.role')}
                  onBlur={(preValue: string, value: string | undefined) => handleBlur(value, preValue, 'role')}
                  onSubmit={(value: string | undefined): void => {
                    if (category?.role !== value && (!!value || !!category?.role)) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                  isArchived={isArchived}
                />
              )}
              {errors?.role?.message && (
                <Text as="span" color="red.500" fontSize="small">
                  {errors?.role?.message}
                </Text>
              )}
            </DetailsCard>

            {/* Temporarily hidden upon request via RRI-2904. Consider removing permanently after June 30 */}
            {/* <Callout title="What is a Category?" variant="bordered">
              Categories are the highest level of your journey, helping you categorize actions into distinct buckets.
              Downstream, Projects can also be attached to Categories, creating a relationship between your Project,
              it's Actions, and their overall Category.
            </Callout> */}
          </SimpleGrid>

          <DetailsCard
            color={isArchived ? IS_ARCHIVE_RGB : category?.color}
            sectionTitle={t('sections.magnificent7.sectionTitles.myUltimatePurpose')}
            icon={IconStarComet}
          >
            <VStack alignItems="normal" width="full" spacing={rem(24)}>
              <Controller
                name="ultimatePurpose"
                control={control}
                render={({ field }) =>
                  isArchived && !defaultValues?.ultimatePurpose ? (
                    <Text textStyle="smallBlack" width="100%" color="gray.400" textAlign="center">
                      {t('sections.magnificent7.emptyStates.noMyUltimatePurposeAdded')}
                    </Text>
                  ) : (
                    <PurposeSelect
                      {...field}
                      behavior={PURPOSE_SELECT_BEHAVIOR_CATEGORY}
                      category={category || undefined}
                      placeholder={t('sections.magnificent7.placeholders.ultimatePurpose')}
                      hideLabel={true}
                      label={t('sections.magnificent7.sectionTitles.myUltimatePurpose')}
                      error={errors?.ultimatePurpose?.message}
                      fontSize={rem(17)}
                      opacity={1}
                      isDisabled={isArchived}
                      color="text-primary"
                      borderBottomStyle={isArchived ? 'none' : 'solid'}
                      _placeholder={{
                        color: 'text-tertiary',
                        opacity: 0.5,
                        fontSize: rem(17),
                        fontWeight: 500,
                      }}
                    />
                  )
                }
              />
            </VStack>
          </DetailsCard>

          <SimpleGrid
            gap={rem(16)}
            width="full"
            columns={{
              base: 1,
              lg: 2,
            }}
          >
            <DetailsCard
              color={isArchived ? IS_ARCHIVE_RGB : category?.color}
              sectionTitle={t('sections.magnificent7.sectionTitles.oneYearGoals')}
              icon={IconMountainTop}
            >
              {isArchived && !defaultValues?.oneYearGoal ? (
                <Text textStyle="smallBlack" width="100%" color="gray.400" textAlign="center">
                  {t('sections.magnificent7.emptyStates.noOneYearGoalsAdded')}
                </Text>
              ) : (
                <EditableTextnput
                  isHidden={isArchived && !defaultValues?.oneYearGoal}
                  cursor={isArchived ? 'not-allowed' : undefined}
                  control={control}
                  fontSize={rem(17)}
                  isSubmitting={isArchived || isSubmitting}
                  name="oneYearGoal"
                  inputType="textarea"
                  placeholder={t('sections.magnificent7.placeholders.oneYearGoal')}
                  onBlur={(preValue: string, value: string | undefined) => handleBlur(value, preValue, 'oneYearGoal')}
                  onSubmit={(value: string | undefined): void => {
                    if (category?.oneYearGoal !== value && (!!value || !!category?.oneYearGoal)) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                  isArchived={isArchived}
                />
              )}
              {errors?.oneYearGoal?.message && (
                <Text as="span" color="red.500" fontSize="small">
                  {errors?.oneYearGoal?.message}
                </Text>
              )}
            </DetailsCard>

            <DetailsCard
              color={isArchived ? IS_ARCHIVE_RGB : category?.color}
              sectionTitle={t('sections.magnificent7.sectionTitles.ninetyDayGoals')}
              icon={IconMountainTop}
            >
              {isArchived && !defaultValues?.ninetyDayGoal ? (
                <Text textStyle="smallBlack" width="100%" color="gray.400" textAlign="center">
                  {t('sections.magnificent7.emptyStates.no90DayGoalsAdded')}
                </Text>
              ) : (
                <EditableTextnput
                  isHidden={isArchived && !defaultValues?.ninetyDayGoal}
                  cursor={isArchived ? 'not-allowed' : undefined}
                  control={control}
                  fontSize={rem(17)}
                  isSubmitting={isArchived || isSubmitting}
                  name="ninetyDayGoal"
                  inputType="textarea"
                  placeholder={t('sections.magnificent7.placeholders.ninetyDayGoal')}
                  onBlur={(preValue: string, value: string | undefined) => handleBlur(value, preValue, 'ninetyDayGoal')}
                  onSubmit={(value: string | undefined): void => {
                    if (category?.ninetyDayGoal !== value && (!!value || !!category?.ninetyDayGoal)) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                  isArchived={isArchived}
                />
              )}
              {errors?.ninetyDayGoal?.message && (
                <Text as="span" color="red.500" fontSize="small">
                  {errors?.ninetyDayGoal?.message}
                </Text>
              )}
            </DetailsCard>
          </SimpleGrid>

          {projectByCategory?.project && (
            <DetailsCard
              color={isArchived ? IS_ARCHIVE_RGB : category?.color}
              sectionTitle={t('sections.magnificent7.sectionTitles.myProjects')}
              icon={IconMyProjects}
            >
              <DndContext
                collisionDetection={closestCenter}
                sensors={sensors}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
              >
                <SortableContext items={projectsOrder} strategy={rectSortingStrategy}>
                  <Grid
                    gridRowGap={rem(25)}
                    gridColumnGap={rem(25)}
                    width="full"
                    style={{
                      gridTemplateColumns: `repeat(${isLargerThan1024 ? '3' : 'auto-fill'}, minmax(300px, 1fr))`,
                    }}
                  >
                    {orderedProjects.map((p) =>
                      p ? (
                        <SortableItemContainer disabled={false} key={p.id} id={p.id}>
                          <div style={{ width: '100%', height: '100%' }}>
                            <ProjectsOverviewCard
                              project={p}
                              location="categories"
                              isArchived={isArchived || p.isArchived}
                              isDragging={activeId === p.id}
                            />
                          </div>
                        </SortableItemContainer>
                      ) : null,
                    )}
                  </Grid>
                </SortableContext>
              </DndContext>
              <Flex justifyContent="center" width="full" marginTop={rem(16)} marginBottom={4}>
                <Button
                  height="auto"
                  isDisabled={isArchived}
                  leftIcon={<Icon as={IconPlus} boxSize={rem(24)} />}
                  onClick={openCreateProjectModal}
                  paddingX={rem(32)}
                  paddingY={rem(16)}
                  variant="secondary"
                >
                  <Text textStyle="large" color="text-primary">
                    {t('sections.magnificent7.buttons.createProject')}
                  </Text>
                </Button>
              </Flex>
            </DetailsCard>
          )}
        </VStack>
      </MotionDiv>

      {isOpenCreateProjectModal && (
        <ManageProjectModal
          isOpen={isOpenCreateProjectModal}
          isLoading={isCreatingProject}
          onSubmit={onCreateProject}
          onClose={onCloseCreateProjectModal}
          categoryId={category?.id}
          readonly
          readonlyTooltipLabel={t('sections.magnificent7.tooltips.readonlyTooltipLabel')}
        />
      )}
    </VStack>
  );
};

export default Magnificent7Section;
